/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { accessAPI, Boton } from "../../shared";

// "Lo que el asistente sabe de mí": las notas activas del usuario y los
// recordatorios que creó para otros admins, con la opción de darlas de baja.

const SECCIONES = [
  { clase: "RECORDATORIO", titulo: "Recordatorios" },
  { clase: "PREFERENCIA", titulo: "Preferencias" },
  { clase: "MEMORIA", titulo: "Datos que recuerda" }
];

// Las fechas de recordatorio son días sin hora (vienen como medianoche UTC):
// se formatean desde el string para no correrse un día por la zona horaria.
function formatearDia(fecha) {
  if (!fecha) return null;
  const [anio, mes, dia] = fecha.slice(0, 10).split("-");
  return `${dia}/${mes}/${anio}`;
}

function nombrePersona(persona) {
  if (!persona) return "";
  return (
    [persona.nombre, persona.apellido].filter(Boolean).join(" ") ||
    persona.email
  );
}

function Nota({ nota, detalle, onBaja }) {
  const etiquetas = (nota.referencias || [])
    .map((r) => r.etiqueta)
    .filter(Boolean);
  return (
    <div
      css={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        padding: "8px 10px",
        borderRadius: "8px",
        backgroundColor: "#f7f7f7"
      }}
    >
      <div css={{ flex: 1, minWidth: 0 }}>
        {nota.titulo && <strong>{nota.titulo}</strong>}
        <div css={{ whiteSpace: "pre-wrap" }}>{nota.texto}</div>
        {(detalle || etiquetas.length > 0) && (
          <div css={{ fontSize: "0.8rem", color: "var(--grisTextos)" }}>
            {[detalle, etiquetas.join(", ")].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
      <Boton
        icono
        rojo
        confirmacion="¿Dar de baja esta nota?"
        callback={() => onBaja(nota.id)}
      >
        <i className="pi pi-trash"></i>
      </Boton>
    </div>
  );
}

export default function MemoriaAsistente({ mostrarError }) {
  const [notas, setNotas] = useState(null);

  useEffect(() => {
    accessAPI(
      "GET",
      "admin/asistente/notas",
      null,
      (respuesta) => setNotas(respuesta),
      (error) => {
        setNotas({ mias: [], paraOtros: [], umbrales: [] });
        mostrarError(error, "No se pudo cargar la memoria del asistente.");
      }
    );
    // Se carga una vez por apertura de la vista
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function darDeBaja(id) {
    accessAPI(
      "DELETE",
      `admin/asistente/notas/${id}`,
      null,
      () =>
        setNotas((prev) => ({
          ...prev,
          mias: prev.mias.filter((n) => n.id !== id),
          paraOtros: prev.paraOtros.filter((n) => n.id !== id)
        })),
      (error) => mostrarError(error, "No se pudo dar de baja la nota.")
    );
  }

  function restablecerUmbral(clave) {
    accessAPI(
      "DELETE",
      `admin/asistente/umbrales/${clave}`,
      null,
      () =>
        setNotas((prev) => ({
          ...prev,
          umbrales: prev.umbrales.filter((u) => u.clave !== clave)
        })),
      (error) => mostrarError(error, "No se pudo restablecer el umbral.")
    );
  }

  if (!notas) {
    return (
      <div css={{ padding: "12px", color: "var(--grisTextos)" }}>
        <i className="pi pi-spin pi-spinner"></i>
      </div>
    );
  }

  const umbrales = notas.umbrales || [];
  const vacia =
    !notas.mias.length && !notas.paraOtros.length && !umbrales.length;

  function detalleDe(nota) {
    const partes = [];
    if (nota.fecha) {
      partes.push(
        nota.fechaDisparo
          ? `Avisado el ${formatearDia(nota.fecha)}`
          : `Para el ${formatearDia(nota.fecha)}`
      );
    }
    if (nota.autorId && nota.autor && nota.autorId !== nota.destinatarioId) {
      partes.push(`De ${nombrePersona(nota.autor)}`);
    }
    return partes.join(" · ");
  }

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: "12px"
      }}
    >
      {vacia && (
        <div css={{ color: "var(--grisTextos)" }}>
          Todavía no hay notas guardadas.
        </div>
      )}

      {SECCIONES.map(({ clase, titulo }) => {
        const deLaClase = notas.mias.filter((n) => n.clase === clase);
        if (!deLaClase.length) return null;
        return (
          <div key={clase}>
            <strong>{titulo}</strong>
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                marginTop: "6px"
              }}
            >
              {deLaClase.map((nota) => (
                <Nota
                  key={nota.id}
                  nota={nota}
                  detalle={detalleDe(nota)}
                  onBaja={darDeBaja}
                />
              ))}
            </div>
          </div>
        );
      })}

      {umbrales.length > 0 && (
        <div>
          <strong>Umbrales de avisos</strong>
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              marginTop: "6px"
            }}
          >
            {umbrales.map((umbral) => (
              <div
                key={umbral.clave}
                css={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  backgroundColor: "#f7f7f7"
                }}
              >
                <div css={{ flex: 1, minWidth: 0 }}>
                  <div>{umbral.titulo}</div>
                  {Object.entries(umbral.parametros).map(([nombre, valor]) => (
                    <div
                      key={nombre}
                      css={{ fontSize: "0.8rem", color: "var(--grisTextos)" }}
                    >
                      {umbral.descripciones[nombre] || nombre}: {String(valor)}
                    </div>
                  ))}
                </div>
                <Boton
                  icono
                  rojo
                  confirmacion="¿Volver a los valores por defecto?"
                  callback={() => restablecerUmbral(umbral.clave)}
                >
                  <i className="pi pi-trash"></i>
                </Boton>
              </div>
            ))}
          </div>
        </div>
      )}

      {notas.paraOtros.length > 0 && (
        <div>
          <strong>Recordatorios que creaste para otros</strong>
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              marginTop: "6px"
            }}
          >
            {notas.paraOtros.map((nota) => (
              <Nota
                key={nota.id}
                nota={nota}
                detalle={[
                  `Para ${nombrePersona(nota.destinatario)}`,
                  nota.fecha && `el ${formatearDia(nota.fecha)}`
                ]
                  .filter(Boolean)
                  .join(" ")}
                onBaja={darDeBaja}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
